import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, X, Plus, Minus } from 'lucide-react';

import useQuestionnaire from 'hooks/useQuestionnaire';
import { QUESTIONNAIRE_CODE } from 'lib/constants';
import { checkSpanishLocale, cn } from 'lib/utils';
import { useGetQuestionnaireOptionSearchV3 } from 'services/profile/hooks';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';
import { Button } from 'components/ui/button';
import MedicationSearch from './components/MedicationSearch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Input } from 'components/ui/input';
import { t } from 'i18next';

export interface Medication {
  name: string;
  doses: { id: string; dose: string }[];
  selectedDose: string;
  quantity: number;
}

export default function SpecifyMedications() {
  const questionData = useQuestionnaireStore((store) => store.questionData);

  const isSpanish = checkSpanishLocale();
  const { onSubmit, isLoading } = useQuestionnaire();

  const [medicationWithDoses, setMedicationWithDoses] = useState<Medication[]>(
    []
  );
  const [openSearch, setOpenSearch] = useState(false);

  const { data: searchOptions } = useGetQuestionnaireOptionSearchV3({
    code: questionData?.code || QUESTIONNAIRE_CODE.SPECIFY_MEDICATION,
    search_text: '',
  });

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const answer = questionData?.choice;
      let parsedAnswer: Medication[] = [];
      try {
        const parsed = JSON.parse(answer as string);
        parsedAnswer = parsed.filter(
          (item: Medication) => !item.name || item.name !== 'None'
        );
      } catch (error) {
        console.log(error);
      }
      setMedicationWithDoses(parsedAnswer);
    }
  }, [questionData]);

  const onRemoveMedicine = (index: number) => {
    setMedicationWithDoses(medicationWithDoses.filter((_, i) => i !== index));
  };

  const onClearAll = () => {
    setMedicationWithDoses([]);
  };

  const onUpdateMedicineQuantity = (index: number, quantity: number) => {
    const updatedMedications = [...medicationWithDoses];
    updatedMedications[index].quantity = quantity;
    setMedicationWithDoses(updatedMedications);
  };

  const onUpdateSelectedDose = (index: number, selectedDose: string) => {
    const updatedMedications = [...medicationWithDoses];
    updatedMedications[index].selectedDose = selectedDose;
    setMedicationWithDoses(updatedMedications);
  };

  const onDoseInputChange = (index: number, value: string) => {
    const updatedMedications = [...medicationWithDoses];
    updatedMedications[index].selectedDose = value;
    setMedicationWithDoses(updatedMedications);
  };

  const onContinue = async () => {
    const formattedMedicines = medicationWithDoses.map((medicine) => {
      const selectedDose = medicine.doses?.length
        ? medicine.doses.find((dose) => dose.id === medicine.selectedDose)
        : { dose: medicine.selectedDose };
      return {
        id: medicine.doses?.length ? medicine.selectedDose : null,
        choice: `${medicine.name}-${selectedDose?.dose}-${medicine.quantity}`,
      };
    });
    const englishSelectedMedications = isSpanish ? null : formattedMedicines;
    const spanishSelectedMedications = isSpanish ? formattedMedicines : null;
    onSubmit({
      choice_value: englishSelectedMedications,
      spanish_choice_value: spanishSelectedMedications,
      questionData: questionData,
      code: questionData?.code,
    });
  };

  // const disableUpdate = useMemo(
  //   () =>
  //     Boolean(
  //       medicationWithDoses?.length &&
  //         medicationWithDoses?.some((medicine) => !medicine.selectedDose)
  //     ),
  //   [medicationWithDoses]
  // );

  return (
    <>
      <QuestionnaireContainer
        questionData={questionData}
        isLoading={isLoading}
        onContinue={onContinue}
        questionNumber={14}
      >
        <button
          onClick={() => setOpenSearch(true)}
          className='flex items-center w-full gap-2 px-4 text-sm text-gray-500 bg-gray-100 rounded-md md:w-2/3 h-9'
        >
          <Search className='size-4' />
          <span className='pt-0.5 font-medium'>{t('search_here')}...</span>
        </button>
        <div className='mt-4'>
          <div className='flex items-center justify-between'>
            <h1 className='font-semibold'>{t('selected')}</h1>
            <Button
              variant='link'
              className='text-sm underline transition-all duration-300 hover:no-underline text-rose-500 h-9'
              disabled={!medicationWithDoses.length}
              onClick={onClearAll}
            >
              {t('clear_all')}
            </Button>
          </div>
          <div>
            {medicationWithDoses?.length ? (
              <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                {medicationWithDoses.map((medication, index) => {
                  return (
                    <MedicationItem
                      item={medication}
                      key={medication.name}
                      onUpdateQuantity={(quantity: number) =>
                        onUpdateMedicineQuantity(index, quantity)
                      }
                      onUpdateSelectedDose={(id: string) =>
                        onUpdateSelectedDose(index, id)
                      }
                      onDoseInputChange={(value: string) =>
                        onDoseInputChange(index, value)
                      }
                      onRemove={() => onRemoveMedicine(index)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className='flex flex-wrap'>
                <div
                  className={cn(
                    'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150'
                  )}
                >
                  <p className='leading-5'>{t('none')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </QuestionnaireContainer>

      <MedicationSearch
        open={openSearch}
        onClose={() => setOpenSearch(false)}
        setSelected={setMedicationWithDoses}
        searchOptions={searchOptions as any}
      />
    </>
  );
}

interface MedicationItemProps {
  item: Medication;
  onUpdateQuantity: (quantity: number) => void;
  onUpdateSelectedDose: (id: string) => void;
  onDoseInputChange: (value: string) => void;
  onRemove: () => void;
}
function MedicationItem({
  item,
  onUpdateQuantity,
  onUpdateSelectedDose,
  onDoseInputChange,
  onRemove,
}: MedicationItemProps) {
  const selectedDose = item.doses
    ? item.doses.find((medicine) => medicine.id === item.selectedDose)
    : null;

  const onSubtractQuantity = () => {
    if (+item.quantity > 1) {
      onUpdateQuantity(+item.quantity - 1);
    }
  };

  const onAddQuantity = () => {
    if (+item.quantity < 5) {
      onUpdateQuantity(+item.quantity + 1);
    }
  };

  return (
    <div className='p-4 space-y-2 bg-gray-100 rounded-xl'>
      <motion.div
        className={cn(
          'border border-gray-300 text-sm font-medium text-[#656D76] px-4 rounded-lg py-2 min-h-8 flex items-center justify-between cursor-pointer transition-all duration-150 gap-1.5'
        )}
        whileTap={{ scale: 0.9 }}
        transition={{ duration: 0.15 }}
        onClick={onRemove}
      >
        <p className='leading-5'>{item.name}</p>
        <X className='w-4 h-4' />
      </motion.div>
      <div className='flex items-center justify-between'>
        {item.doses.length > 1 ? (
          <Select
            value={item.selectedDose}
            onValueChange={onUpdateSelectedDose}
          >
            <SelectTrigger className='w-[180px] h-9 rounded-lg text-sm'>
              <SelectValue placeholder='Theme' />
            </SelectTrigger>
            <SelectContent>
              {item.doses.map((dose) => (
                <SelectItem key={dose.dose} value={dose.id}>
                  {dose.dose}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : item.doses.length === 1 ? (
          <div className='px-3 py-2 text-sm border border-input bg-background w-[180px] h-9 rounded-lg'>
            <p>{selectedDose?.dose}</p>
          </div>
        ) : (
          <Input
            className='w-[180px] h-9 rounded-lg text-sm border-input shadow-none border-[1px]'
            value={item.selectedDose}
            onChange={(e) => onDoseInputChange(e.target.value)}
            placeholder='Specify Dosage'
          />
        )}
        <div className='flex items-center'>
          <button
            className={cn(
              'flex items-center justify-center rounded-full size-5 bg-primary',
              item.quantity <= 1 && 'cursor-not-allowed bg-gray-400'
            )}
            onClick={onSubtractQuantity}
          >
            <Minus className='text-white size-3' />
          </button>
          <p className='w-6 text-sm font-medium text-center'>{item.quantity}</p>
          <button
            className={cn(
              'flex items-center justify-center rounded-full size-5 bg-primary',
              item.quantity >= 5 && 'cursor-not-allowed bg-gray-400'
            )}
            onClick={onAddQuantity}
          >
            <Plus className='text-white size-3' />
          </button>
        </div>
      </div>
    </div>
  );
}
